import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Send, CheckCircle, Copy } from 'lucide-react';
import PageHero from '../components/PageHero';
import { COMPANY } from '../config/siteContent';
import useDocumentTitle from '../hooks/useDocumentTitle';

/**
 * Contact - three-column contact info, embedded Google Map, and a contact form.
 *
 * Submission flow (May 2026):
 *   1. POST to the server-side handler (contact.ashx on IIS), which relays the
 *      message through iConnect's own mail server to Sales - no visitor email
 *      app needed. On {ok:true} the visitor sees a clean "Message sent".
 *   2. If the handler isn't reachable yet (not deployed/enabled) or errors, we
 *      fall back to opening the visitor's mail app AND always show a panel with
 *      the Sales address, phone, and a one-click copy - so it's never a dead end.
 *
 * Backend setup lives in /server/SETUP-CONTACT-BACKEND.md (for IT/Jonathan).
 */
const Contact = () => {
  useDocumentTitle({
    title: 'Contact Us',
    description:
      'Reach iConnect sales at (877) 327-9334 or Sales@iConnect-Corp.com. Our headquarters is at 24 Frank Lloyd Wright Drive, Ann Arbor, MI 48106.',
  });
  const [sending, setSending] = useState(false); // request in flight
  const [sent, setSent] = useState(false); // backend accepted + emailed
  const [submitted, setSubmitted] = useState(false); // fell back to mailto/copy panel
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '', company: '' });

  // Where the form delivers. RECIPIENT is Sales for now (Support can be added
  // later). The server-side handler is contact.ashx in this same folder on IIS
  // (see /server/SETUP-CONTACT-BACKEND.md). It relays the message through
  // iConnect's own mail server - no visitor email app required.
  const RECIPIENT = COMPANY.emails.sales;
  const ENDPOINT = `${process.env.PUBLIC_URL || ''}/contact.ashx`;

  const composedMessage = () =>
    `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    // 1) Try the real backend. Once IT enables contact.ashx it returns
    //    {"ok":true} and the visitor sees a clean "Message sent" - no email app.
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        body: new URLSearchParams({
          name: form.name,
          email: form.email,
          message: form.message,
          company: form.company, // honeypot - real users leave this blank
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      // The handler returns JSON {ok:true}. Until it's live, the SPA rewrite
      // serves index.html (HTML) for this path, so json() fails -> we fall back.
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.ok) {
        setSent(true);
        setSending(false);
        return;
      }
    } catch (_) {
      /* backend not reachable yet / aborted - fall through to the safety net */
    }

    // 2) Fallback (backend not live yet or errored): open the visitor's mail
    //    app to Sales, and always show the copy/phone panel so it's never dead.
    const subject = encodeURIComponent(`Website inquiry from ${form.name}`);
    const body = encodeURIComponent(composedMessage());
    try {
      window.location.href = `mailto:${RECIPIENT}?subject=${subject}&body=${body}`;
    } catch (_) {
      /* no mail handler or navigation blocked - the panel below is the net */
    }
    setSubmitted(true);
    setSending(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(composedMessage());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const contactBlocks = [
    {
      label: 'Main Office',
      Icon: Phone,
      lines: [COMPANY.phones.general, `Fax: ${COMPANY.phones.fax}`],
      href: `tel:${COMPANY.phones.general.replace(/\D/g, '')}`,
    },
    {
      label: 'Sales',
      Icon: Phone,
      lines: [COMPANY.phones.salesTollFree, COMPANY.emails.sales],
      href: `mailto:${COMPANY.emails.sales}`,
    },
    {
      label: 'Support',
      Icon: Phone,
      lines: [COMPANY.phones.supportTollFree, COMPANY.emails.support],
      href: `mailto:${COMPANY.emails.support}`,
    },
  ];

  // Google Maps embed URL for the address
  const mapQuery = encodeURIComponent(
    `${COMPANY.address.street}, ${COMPANY.address.city}, ${COMPANY.address.state} ${COMPANY.address.zip}`
  );
  const mapSrc = `https://maps.google.com/maps?q=${mapQuery}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's talk"
        titleAccent="EDI."
        breadcrumbs={[{ label: 'Contact' }]}
        subhead={`Reach us at ${COMPANY.phones.salesTollFree}, email ${COMPANY.emails.sales}, or fill out the form below - we typically respond within one business day.`}
      />

      <section className="py-16 lg:py-20 bg-bg-canvas">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
          {/* 3-col contact info */}
          <div className="grid sm:grid-cols-3 gap-5 mb-12">
            {contactBlocks.map((block) => (
              <a
                key={block.label}
                href={block.href}
                className="card-elevated p-6 group"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-sky shadow-glow-sky flex items-center justify-center mb-4">
                  <block.Icon size={20} className="text-white" />
                </div>
                <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-accent-deep font-bold mb-2">
                  {block.label}
                </div>
                {block.lines.map((line, i) => (
                  <div
                    key={i}
                    className={i === 0 ? 'font-display font-bold text-text-primary' : 'text-sm text-text-secondary'}
                  >
                    {line}
                  </div>
                ))}
              </a>
            ))}
          </div>

          {/* Address + Map + Form */}
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Address + Map */}
            <div className="lg:col-span-7 space-y-6">
              <div className="card-elevated p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-accent-soft flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-accent-primary" />
                  </div>
                  <div>
                    <p className="eyebrow !mb-1">Headquarters</p>
                    <p className="font-display font-bold text-xl text-text-primary leading-tight">
                      {COMPANY.fullName}
                    </p>
                    <p className="text-text-secondary mt-1">
                      {COMPANY.address.street}
                      <br />
                      {COMPANY.address.poBox}
                      <br />
                      {COMPANY.address.city}, {COMPANY.address.state}{' '}
                      {COMPANY.address.zip}
                    </p>
                  </div>
                </div>
              </div>

              <div className="card-elevated overflow-hidden">
                <iframe
                  title="iConnect office map"
                  src={mapSrc}
                  className="w-full"
                  style={{ height: 380, border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-5">
              <motion.form
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="card-elevated p-6 lg:p-8 sticky top-28"
              >
                <p className="eyebrow">Get in touch</p>
                <h3 className="font-display font-bold text-2xl text-text-primary mb-6 tracking-tight">
                  Send us a message
                </h3>

                {sent ? (
                  /* Real success: the backend accepted and emailed Sales. */
                  <div className="text-center py-6">
                    <CheckCircle
                      size={40}
                      className="text-accent-primary mx-auto mb-3"
                    />
                    <p className="text-text-primary font-semibold text-lg">
                      Message sent
                    </p>
                    <p className="text-sm text-text-secondary mt-1">
                      Thanks{form.name ? `, ${form.name}` : ''} - your message is on
                      its way to our sales team. We reply within one business day.
                    </p>
                  </div>
                ) : submitted ? (
                  /* Fallback: backend not reachable, so we opened the mail app and
                     always show a direct route so the form is never a dead end. */
                  <div className="py-2">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle size={22} className="text-accent-primary flex-shrink-0" />
                      <p className="text-text-primary font-semibold">Almost done</p>
                    </div>
                    <p className="text-sm text-text-secondary mb-4">
                      We tried to open your email app with the message ready to send.
                      If nothing opened, no problem - reach us directly, or copy your
                      message and send it from wherever you read email. We reply within
                      one business day.
                    </p>
                    <div className="space-y-2 mb-4">
                      <a
                        href={`mailto:${COMPANY.emails.sales}`}
                        className="flex items-center gap-2 text-sm text-accent-primary font-semibold hover:underline"
                      >
                        <Send size={14} className="flex-shrink-0" />
                        {COMPANY.emails.sales}
                      </a>
                      <a
                        href={`tel:${COMPANY.phones.salesTollFree.replace(/\D/g, '')}`}
                        className="flex items-center gap-2 text-sm text-text-secondary hover:text-accent-primary"
                      >
                        <Phone size={14} className="flex-shrink-0" />
                        {COMPANY.phones.salesTollFree}
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="btn-secondary w-full justify-center"
                    >
                      {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
                      {copied ? 'Copied to clipboard' : 'Copy my message'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Input
                      label="Name"
                      value={form.name}
                      onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                      required
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                      required
                    />
                    <TextArea
                      label="How can we help?"
                      value={form.message}
                      onChange={(v) => setForm((f) => ({ ...f, message: v }))}
                      required
                    />
                    {/* Honeypot: hidden from people, tempting to bots. Kept
                        off-screen (not display:none) so bots still fill it. */}
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      value={form.company}
                      onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                    />
                    <button
                      type="submit"
                      disabled={sending}
                      className="btn-primary w-full justify-center group disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {sending ? 'Sending…' : 'Send message'}
                      <Send size={14} />
                    </button>
                  </div>
                )}
              </motion.form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const Input = ({ label, value, onChange, type = 'text', required }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-text-primary mb-1.5">
      {label}
      {required && <span className="text-accent-primary"> *</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full px-4 py-2.5 rounded-lg border border-border-subtle bg-white text-text-primary focus:outline-none focus:border-accent-primary focus:ring-2 focus:ring-accent-primary/20 transition-all"
    />
  </label>
);

const TextArea = ({ label, value, onChange, required }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-text-primary mb-1.5">
      {label}
      {required && <span className="text-accent-primary"> *</span>}
    </span>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      rows={4}
      className="w-full px-4 py-2.5 rounded-lg border border-border-subtle bg-white text-text-primary focus:outline-none focus:border-accent-primary focus:ring-2 focus:ring-accent-primary/20 transition-all resize-none"
    />
  </label>
);

export default Contact;
